import { useState } from "react";

import { Button } from "#/components/ui/button";
import { Checkbox } from "#/components/ui/checkbox";
import { Field } from "#/components/ui/field";
import { Label } from "#/components/ui/label";
import { cn } from "#/lib/utils";

import {
  gameDockedPanelFooterClassName,
  gameDockedPanelSurfaceClassName,
  gameDockedPanelTitleClassName,
} from "./GameDockedPanelShell";

const suppressCheckboxId = "capture-reset-suppress-checkbox";
const suppressLabelId = "capture-reset-suppress-label";

function SuppressFutureField({
  checked,
  disabled,
  onCheckedChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  const toggleChecked = () => {
    if (disabled) return;
    onCheckedChange(!checked);
  };

  return (
    <Field orientation="horizontal" className="w-fit" data-disabled={disabled ? true : undefined}>
      <Checkbox
        id={suppressCheckboxId}
        name={suppressCheckboxId}
        aria-labelledby={suppressLabelId}
        checked={checked}
        onCheckedChange={(nextChecked) => onCheckedChange(nextChecked === true)}
        disabled={disabled}
      />
      <Label
        id={suppressLabelId}
        className="cursor-pointer font-normal"
        onPointerDown={(event) => {
          if (disabled) return;
          event.preventDefault();
          toggleChecked();
        }}
      >
        Don&apos;t show this again
      </Label>
    </Field>
  );
}

type CaptureResetWarningProps = {
  onConfirm: (suppressFuture: boolean) => void;
  onCancel: () => void;
  disabled?: boolean;
  className?: string;
};

export function CaptureResetWarning({
  onConfirm,
  onCancel,
  disabled,
  className,
}: CaptureResetWarningProps) {
  const [suppressFuture, setSuppressFuture] = useState(false);

  return (
    <div
      role="dialog"
      aria-label="Cancel capture warning"
      className={cn(gameDockedPanelSurfaceClassName, "w-fit", className)}
    >
      <p className={gameDockedPanelTitleClassName}>Cancel capture?</p>
      <p className="text-muted-foreground">Moving off this building resets capture progress.</p>
      <SuppressFutureField
        checked={suppressFuture}
        disabled={disabled}
        onCheckedChange={setSuppressFuture}
      />
      <div className={cn(gameDockedPanelFooterClassName, "flex-row")}>
        <Button
          onClick={onCancel}
          disabled={disabled}
          variant="outline"
          size="default"
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(suppressFuture)}
          disabled={disabled}
          variant="default"
          size="default"
          className="flex-1"
        >
          OK
        </Button>
      </div>
    </div>
  );
}
